import { useState } from "react";
import { Camera, Loader2, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

/** Generic photo viewer for proof-of-pickup or proof-of-delivery.
 * `kind`: "pickup" | "delivery"
 *
 * Phase 44.23 — Lazy load mirato (memory pressure iOS Safari).
 *
 * Root cause schermata bianca post-pickup confermato dall'utente:
 *   accepted/picking_up → no ProofPhotoView → no schermata bianca al ritorno da Maps
 *   picked_up/delivering → ProofPhotoView monta → fetch blob base64 3-6MB +
 *   <img src="data:..."> mantenuto in memoria + GPS polling + WebGL Google Maps
 *   = memory pressure iOS → Safari uccide la tab → al ritorno X bianca.
 *
 * Fix: la foto NON viene fetchata né renderizzata fino al click esplicito
 * dell'utente. Card collapsed di default con header + bottone "Mostra foto".
 * In fase di consegna il driver NON ha bisogno di rivedere la propria foto
 * di ritiro (è una verifica eventuale, non un'azione operativa). Da delivered
 * in poi mostra di default (lo storico merita).
 */
export default function ProofPhotoView({ orderId, kind = "delivery", testid, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [fetched, setFetched] = useState(false);

  const tid = testid || `${kind}-photo`;
  const label = kind === "pickup" ? "Foto ritiro" : "Foto consegna";
  const noteKey = kind === "pickup" ? "pickup_note" : "delivery_note";
  const byKey = kind === "pickup" ? "picked_up_by_name" : "delivered_by_name";
  const atKey = kind === "pickup" ? "picked_up_at" : "delivered_at";

  const fetchPhoto = async () => {
    if (fetched) return;
    setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get(`/orders/${orderId}/${kind}-photo`);
      setData(d);
      setFetched(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !fetched) await fetchPhoto();
  };

  // Auto-fetch only when defaultExpanded=true (delivered state)
  if (defaultExpanded && !fetched && !loading && !err) {
    fetchPhoto();
  }

  return (
    <div className="card-soft p-5 sm:p-6 space-y-3" data-testid={tid}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="overline inline-flex items-center gap-1.5">
          <Camera className="h-3.5 w-3.5" /> {label}
        </p>
        {data?.[atKey] && (
          <span className="text-xs text-dapnow-muted">{formatDateTime(data[atKey])}</span>
        )}
      </div>

      {!expanded ? (
        <button
          type="button"
          onClick={toggle}
          data-testid={`${tid}-toggle`}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-dapnow-line bg-white text-sm font-semibold text-dapnow-ink hover:bg-dapnow-bg transition"
        >
          <Eye className="h-4 w-4" /> Mostra foto
        </button>
      ) : loading ? (
        <p className="text-sm text-dapnow-muted inline-flex items-center gap-2" data-testid={`${tid}-loading`}>
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
        </p>
      ) : err || !data?.data_base64 ? (
        <p className="text-sm text-dapnow-muted" data-testid={`${tid}-empty`}>
          Nessuna foto disponibile.
        </p>
      ) : (
        <>
          <img
            src={data.data_base64.startsWith("data:") ? data.data_base64 : `data:image/jpeg;base64,${data.data_base64}`}
            alt={label}
            data-testid={`${tid}-img`}
            className="w-full max-h-96 object-cover rounded-2xl border border-dapnow-line"
          />
          {data[byKey] && (
            <p className="text-xs text-dapnow-muted" data-testid={`${tid}-driver`}>
              {kind === "pickup" ? "Ritirato da" : "Consegnato da"} <strong className="text-dapnow-ink">{data[byKey]}</strong>
            </p>
          )}
          {data[noteKey] && (
            <p className="text-sm text-dapnow-ink bg-dapnow-bg rounded-xl p-3" data-testid={`${tid}-note`}>
              “{data[noteKey]}”
            </p>
          )}
          {!defaultExpanded && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              data-testid={`${tid}-collapse`}
              className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl text-xs font-semibold text-dapnow-muted hover:text-dapnow-ink"
            >
              <EyeOff className="h-3.5 w-3.5" /> Nascondi foto
            </button>
          )}
        </>
      )}
    </div>
  );
}
